import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, TrendingDown, TrendingUp, Target, ChefHat, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MealRecommendations = ({ token }) => {
  const { t } = useTranslation();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/recommendations/suggest-meals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <div className="text-center py-8">
          <div className="animate-pulse">
            <Sparkles size={36} className="mx-auto mb-4 text-[#FF6600]" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Generating personalized meal suggestions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <div className="text-center py-8">
          <p className="text-red-500 text-sm">{error}</p>
          <button onClick={fetchRecommendations} className="mt-4 px-4 py-2 bg-[#FF6600] text-white rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!recommendations) return null;

  const GoalIcon = recommendations.goal === 'lose weight' ? TrendingDown : 
                    recommendations.goal === 'gain weight' ? TrendingUp : Target;

  return (
    <div className="space-y-4" data-testid="meal-recommendations">
      {/* Status Card */}
      <div className="bg-white dark:bg-gray-800 border-2 border-orange-200 p-4 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
            <Sparkles size={20} className="text-[#FF6600]" />
            Smart Meal Suggestions
          </h2>
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <GoalIcon size={16} className="text-[#FF6600]" />
            {recommendations.goal}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-xs mb-1 text-gray-600 dark:text-gray-300">{t('endOfDay.consumed')}</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">{Math.round(recommendations.consumed_calories)} cal</div>
          </div>
          <div>
            <div className="text-xs mb-1 text-gray-600 dark:text-gray-300">{t('mealRec.remainingCalories')}</div>
            <div className={`text-xl font-bold ${recommendations.remaining_calories < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
              {Math.round(recommendations.remaining_calories)} cal
            </div>
          </div>
          <div>
            <div className="text-xs mb-1 text-gray-600 dark:text-gray-300">{t('endOfDay.target')}</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">{Math.round(recommendations.daily_target)} cal</div>
          </div>
        </div>

        {recommendations.tip && (
          <div className="bg-orange-50 p-4 rounded-lg flex items-start gap-3 border border-orange-200">
            <Lightbulb size={20} className="flex-shrink-0 mt-0.5 text-[#FF6600]" />
            <div>
              <div className="font-semibold mb-1 text-gray-900 dark:text-white text-sm">Nutrition Tip</div>
              <p className="text-xs text-gray-700 dark:text-gray-300">{recommendations.tip}</p>
            </div>
          </div>
        )}
      </div>

      {/* Current Meal Time */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-2 border-[#FF6600]">
        <div className="flex items-center gap-3">
          <ChefHat size={20} className="text-[#FF6600]" />
          <div>
            <div className="font-semibold text-sm">Next Meal: {recommendations.meal_time}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {recommendations.meals_today} meal{recommendations.meals_today !== 1 ? 's' : ''} logged today
            </div>
          </div>
        </div>
      </div>

      {/* Meal Suggestions */}
      {recommendations.suggestions && recommendations.suggestions.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">{t('mealRec.suggestedMeals')}</h3>
          <div className="space-y-4">
            {recommendations.suggestions.map((meal, idx) => (
              <div 
                key={idx} 
                className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-[#FF6600] transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold mb-1">{meal.name}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{meal.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-[#FF6600]">{Math.round(meal.calories)} cal</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.protein')}</div>
                    <div className="font-semibold text-sm">{Math.round(meal.protein)}g</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.carbs')}</div>
                    <div className="font-semibold text-sm">{Math.round(meal.carbs)}g</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.fats')}</div>
                    <div className="font-semibold text-sm">{Math.round(meal.fats)}g</div>
                  </div>
                </div>

                {meal.reasoning && (
                  <div className="bg-orange-50 p-3 rounded-lg border-l-4 border-[#FF6600]">
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      <strong>Why this meal:</strong> {meal.reasoning}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button 
            onClick={fetchRecommendations}
            className="w-full mt-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 flex items-center justify-center gap-2 font-semibold"
          >
            <Sparkles size={18} />
            Generate New Suggestions
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm text-center">
          <Sparkles size={36} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
            No specific meal suggestions available right now.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Log more meals throughout the day to get personalized recommendations!
          </p>
          <button onClick={fetchRecommendations} className="px-4 py-2 bg-[#FF6600] text-white rounded-lg font-semibold">
            Try Again
          </button>
        </div>
      )}

      {/* Macro Balance */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h3 className="font-bold mb-4">Today's Macro Balance</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full bg-red-500"
                style={{ width: `${Math.min((recommendations.macro_balance.protein / 150) * 100, 100)}%` }}
              />
            </div>
            <div className="font-semibold text-sm">{Math.round(recommendations.macro_balance.protein)}g</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.protein')}</div>
          </div>
          <div className="text-center">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full bg-yellow-500"
                style={{ width: `${Math.min((recommendations.macro_balance.carbs / 250) * 100, 100)}%` }}
              />
            </div>
            <div className="font-semibold text-sm">{Math.round(recommendations.macro_balance.carbs)}g</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.carbs')}</div>
          </div>
          <div className="text-center">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full bg-amber-500"
                style={{ width: `${Math.min((recommendations.macro_balance.fats / 70) * 100, 100)}%` }}
              />
            </div>
            <div className="font-semibold text-sm">{Math.round(recommendations.macro_balance.fats)}g</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.fats')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealRecommendations;
